import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Copy, CheckCircle2, Users, DollarSign, Link as LinkIcon, RefreshCw, IdCard, AlertCircle, TrendingUp, LogOut } from 'lucide-react';
import "./Referrals.css";
import { API_BASE } from "../../../config/api";

const Referrals = () => {
  const [user, setUser] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({ count: 0, earnings: 0 });
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ✅ Helper: Get JWT token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // ✅ Check if user is authenticated via JWT
  const isAuthenticated = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    return !!token;
  };

  useEffect(() => {
    if (isAuthenticated()) {
      fetchReferralData();
    } else {
      setLoading(false);
      setError('Please log in to view referrals');
    }
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      setError('');

      const headers = getAuthHeaders();

      console.log('🔗 Fetching referral data with JWT token...');

      // ✅ Fetch user profile with referral code
      const profileResponse = await axios.get(`${API_BASE}/api/identity/profile`, { headers });
      
      if (profileResponse.data.success && profileResponse.data.user) {
        const userData = profileResponse.data.user;
        
        setUser({
          email: userData.email,
          name: userData.fullName || userData.name || userData.email.split('@')[0]
        });
        
        const code = userData.referralCode;
        
        if (code) {
          setReferralCode(code);
          setReferralLink(`${window.location.origin}/#/register?ref=${code}`);
          console.log('✅ Referral code loaded:', code);
        } else {
          // Generate referral code if not exists
          const emailPart = userData.email.split('@')[0].toUpperCase().substring(0, 8);
          const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
          const generatedCode = `ERAX-${emailPart}-${randomPart}`;
          
          setReferralCode(generatedCode);
          setReferralLink(`${window.location.origin}/#/register?ref=${generatedCode}`);
          console.log('⚠️ Generated referral code:', generatedCode);
        }
      }

      // ✅ Fetch referral stats with commission data
      const statsResponse = await axios.get(`${API_BASE}/api/identity/referral-stats`, { headers });
      
      if (statsResponse.data.success) {
        const data = statsResponse.data;
        
        console.log('📊 Referral stats:', data);
        
        setStats({
          count: data.totalReferrals || 0,
          earnings: data.totalEarnings || 0
        });
        
        // ✅ Set the referrals list
        const referralsList = data.referrals || [];
        console.log(`📥 Loaded ${referralsList.length} referrals`);
        setReferrals(referralsList);
        
      } else {
        console.warn('⚠️ Failed to load referral stats');
        setStats({ count: 0, earnings: 0 });
        setReferrals([]);
      }

    } catch (err) {
      console.error("❌ Failed to load referrals:", err);
      
      // Handle 401 - session expired
      if (err.response?.status === 401) {
        console.error('❌ Session expired. Redirecting to login...');
        localStorage.clear();
        setTimeout(() => { window.location.href = '/#/login'; }, 2000);
        setError('Session expired. Redirecting to login...');
        return;
      }
      
      const errorMsg = err.response?.data?.message || err.message || "Failed to load referral data";
      setError(errorMsg);
      
      // Fallback: Generate a temporary code
      if (user) {
        const emailPart = user.email.split('@')[0].toUpperCase().substring(0, 8);
        const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
        const fallbackCode = `ERAX-${emailPart}-${randomPart}`;
        
        setReferralCode(fallbackCode);
        setReferralLink(`${window.location.origin}/#/register?ref=${fallbackCode}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!referralCode) return;
    
    navigator.clipboard.writeText(referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    });
  };

  const handleShare = async () => {
    if (!referralLink) return;
    
    if (navigator.share) {
      try {
        await navigator.share({ 
          title: 'Join EraX', 
          text: 'Earn rewards by inviting friends to EraX', 
          url: referralLink 
        });
      } catch (err) { 
        console.log("Share cancelled or failed");
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleRefresh = () => {
    fetchReferralData();
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.clear();
      window.location.href = '/#/login';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="referrals-container">
        <div className="loading-spinner">
          <RefreshCw size={24} className="spin" />
          <p>Loading referral data...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return (
      <div className="referrals-container">
        <div className="empty-state">
          <AlertCircle size={48} className="empty-icon" />
          <p className="empty-msg">Please log in to view referrals</p>
          <button 
            onClick={() => window.location.href = '/#/login'}
            className="share-btn"
            style={{ marginTop: '16px' }}
          >
            Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="referrals-container">
      <div className="referrals-header">
        <div>
          <h2>Refer & Earn</h2>
          <p className="subtitle">
            {user ? `Welcome, ${user.name}!` : 'Invite friends and earn 3% commission'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleRefresh} className="refresh-btn" title="Refresh">
            <RefreshCw size={18} />
          </button>
          <button onClick={handleLogout} className="refresh-btn" title="Logout" style={{ color: '#ef4444' }}>
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={handleRefresh} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      <div className="link-card">
        <div className="link-header">
          <IdCard size={20} />
          <span>Your Unique EraX ID</span>
        </div>
        
        <div className="link-row">
          <code className="referral-code-display" style={{ letterSpacing: '2px', fontSize: '1.1rem' }}>
            {referralCode || "Loading..."}
          </code>
          <button onClick={handleCopy} className="copy-btn" title="Copy to clipboard" disabled={!referralCode}>
            {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
          </button>
        </div>
        
        {copied && <span className="copied-msg">✓ Copied to clipboard!</span>}
        
        <div className="link-actions">
          <button onClick={handleShare} className="share-btn" disabled={!referralLink}>
            📤 Share Link
          </button>
          {referralLink && (
            <a 
              href={referralLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="share-btn"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <LinkIcon size={16} /> Preview
            </a>
          )}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <Users size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Total Referrals</span>
            <strong className="stat-value">{stats.count}</strong>
          </div>
        </div>
        <div className="stat-card">
          <DollarSign size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Total Earnings</span>
            <strong className="stat-value">${stats.earnings.toFixed(2)}</strong>
          </div>
        </div>
      </div>

      <div className="referrals-list">
        <h3>Recent Referrals ({referrals.length})</h3>
        {referrals.length > 0 ? (
          <div className="referrals-items">
            {referrals.map((ref, index) => (
              <div key={ref.id || ref._id || index} className="referral-item">
                <div className="referral-user">
                  <span className="username">
                    {ref.name || 'Anonymous User'}
                  </span>
                  <span className="referral-date">
                    {formatDate(ref.joinedAt || ref.date)}
                  </span>
                </div>
                <div className="referral-details" style={{ textAlign: 'right' }}>
                  <span className="referral-earnings">
                    +${(ref.earned || 0).toFixed(2)}
                  </span>
                  {ref.depositCount > 0 && (
                    <span className="referral-deposits" style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>
                      {ref.depositCount} deposit{ref.depositCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Users size={48} className="empty-icon" />
            <p className="empty-msg">No referrals yet</p>
            <p className="empty-submsg">Share your EraX ID to get started!</p>
          </div>
        )}
      </div>

      <div className="info-note">
        <h4> How It Works</h4>
        <ul className="commission-list">
          <li>
            <TrendingUp size={14} style={{ marginRight: '8px' }} />
            <strong>Invite Friends</strong> - Share your unique EraX ID
          </li>
          <li>
            <TrendingUp size={14} style={{ marginRight: '8px' }} />
            They make a deposit (min $200) → <span className="reward">3% commission</span>
          </li>
          <li>
            <TrendingUp size={14} style={{ marginRight: '8px' }} />
            Earn on EVERY deposit they make
          </li>
          <li>
            <TrendingUp size={14} style={{ marginRight: '8px' }} />
            Earnings are credited <span className="reward">automatically</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Referrals;